import { getViewerComponentOpacity, type IViewerIfcClass } from "@multiconsult-group/multiplacer-core";
import { IRoomMcBimSpaceCombo } from "./designThunk";
import { IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { PartialMcBimSpace } from "../utils";
import { IRoomsSummaryHashmap } from "@backend/src/shared/schemas/schemas";
import { PartialMcBimItem } from "../McBim/McBimSlice";
import {
  GetMcbimItemsInSpaceApiResponse,
  PostMcbimItems3DByIdsApiResponse,
  PostMcbimItems3DNearCoordinateApiResponse,
  PostMcbimItemsApiResponse,
} from "../../openapi";
import { MMI_COLORS } from "../RoomColoring";
import {
  IfcClassesEnum,
  getDeeplyNestedValue,
  getInstanceClassMeta,
  getInstanceClassZIndex,
} from "@multiconsult-group/multiplacer-core";
import { DISCIPLINES } from "../constants";
import { IMcBimItemColorMode } from "./designSlice";
import {
  inferPrimarySystemKindFromPrimarySystemName,
  PRIMARY_SYSTEM_SPECS,
} from "../systemsConstants";

const CLICKED_STROKE_COLOR = "#FF0000"; // red for highlight
const CLICKED_STROKE_WIDTH = "3";

export type IMcBimItemDisplayColorSource =
  | PostMcbimItemsApiResponse[number]
  | GetMcbimItemsInSpaceApiResponse[number]
  | PostMcbimItems3DByIdsApiResponse[number]
  | PostMcbimItems3DNearCoordinateApiResponse[number];

const getItemMmiValue = (item: IMcBimItemDisplayColorSource, mmiPath?: string): unknown => {
  if (mmiPath) {
    return getDeeplyNestedValue(item, mmiPath);
  }

  return "mmi" in item ? item.mmi : undefined;
};

const getItemDisciplineValue = (item: IMcBimItemDisplayColorSource): unknown =>
  "discipline" in item ? item.discipline : undefined;

const getItemPrimarySystemName = (item: IMcBimItemDisplayColorSource): string | null => {
  if ("primarySystem" in item && item.primarySystem?.name) {
    return item.primarySystem.name;
  }

  if ("system" in item && item.system) {
    return item.system;
  }

  return null;
};

export const filterAndTransformMcBiItemsToPixPolygons = (
  mcbimItems: PartialMcBimItem[],
  mcbimItemColorMode: IMcBimItemColorMode,
  mmiPath?: string,
  clickedMcBimItemId?: string | null,
  ifcClassesToDisplay: readonly IViewerIfcClass[] = []
): IPixPolygon[] => {
  if (!mcbimItems?.length) return [];

  // 1) Konvertera (men spara z + originalindex fÃ¶r stabil sort)
  const convertedWithOrder = mcbimItems.reduce(
    (acc, mcbimItem, index) => {
      if (!mcbimItem?.bbox && !mcbimItem?.polygon) return acc;

      const converted = convertMcBimItemToPixPolygons({
        mcBimItem: mcbimItem,
        mcbimItemColorMode,
        mmiPath,
        clickedId: clickedMcBimItemId ?? undefined,
      });

      if (!converted) return acc;

      converted.id = `mcbim-item-${index}`;
      converted.entityId = mcbimItem._id;
      converted.opacity = getViewerComponentOpacity(ifcClassesToDisplay, mcbimItem.instanceClass);

      acc.push({
        poly: converted,
        z: getInstanceClassZIndex(mcbimItem.instanceClass ?? ""),
        i: index,
      });

      return acc;
    },
    [] as Array<{ poly: IPixPolygon; z: number; i: number }>
  );

  // 2) Sortera sÃ¥ vÃ¤ggar fÃ¶rst, dÃ¶rr/fÃ¶nster Ã¶ver, osv.
  convertedWithOrder.sort((a, b) => {
    if (a.z !== b.z) return a.z - b.z;
    return a.i - b.i;
  });

  // 3) Returnera bara polygons
  return convertedWithOrder.map((x) => x.poly);
};

export interface IItemLike {
  mmi?: string | null;
  // ifcColor?: string; // later
}

export const convertMcBimItemToPixPolygons = (args: {
  mcBimItem: PostMcbimItemsApiResponse[number];
  mcbimItemColorMode: IMcBimItemColorMode;
  mmiPath?: string;
  clickedId?: string;
}): IPixPolygon | undefined => {
  const { mcBimItem, mcbimItemColorMode, clickedId } = args;

  if (mcBimItem == null) return undefined;
  if (mcBimItem.polygon == null && mcBimItem.bbox == null) return undefined;

  const isClicked = Boolean(clickedId) && clickedId === mcBimItem._id;
  const fillColor =
    getMcBimItemDisplayColor({ mcbimItemColorMode, item: mcBimItem }) ??
    getMcBimItemDisplayColor({ mcbimItemColorMode: "MULTIPLACER_COLORS", item: mcBimItem }) ??
    "#000000";
  let strokeColor = "#000000";
  let strokeWidth = "1";

  if (isClicked) {
    strokeColor = CLICKED_STROKE_COLOR;
    strokeWidth = CLICKED_STROKE_WIDTH;
  }

  return {
    id: mcBimItem.globalId,
    points: (mcBimItem.bbox ?? mcBimItem.polygon) as number[][],
    fillColor,
    strokeColor,
    strokeWidth,
    text: {
      values: [mcBimItem.name],
      color: "#000000",
      fontSize: "1",
      show: false,
    },
    isMoveable: false,
    tooltip: (() => {
      const compCode = mcBimItem.compCode?.trim();
      const name = mcBimItem.name?.trim();
      const ifcClass = mcBimItem.instanceClass?.trim();
      const typeName = mcBimItem.typeName?.trim();
      const system = mcBimItem.system?.trim();

      const lines = [
        compCode || null,
        name || null,
        ifcClass || null,
        typeName || null,
        system || null,
      ].filter((v): v is string => Boolean(v));

      return lines.length ? lines.join("\n") : undefined;
    })(),
    isDragSelectable: false,
    isNotClickable: false,
  };
};

// Norsk (BokmÃ¥l): Hjelper som tÃ¥ler "E ", "e", osv.
const normalizeDisciplineCode = (raw: unknown): string | null => {
  if (typeof raw !== "string") return null;
  const c = raw.trim().toUpperCase();
  if (!c) return null;
  // vi forventer egentlig Ã©n bokstav, men tar fÃ¸rste for Ã¥ vÃ¦re robust
  return c[0] ?? null;
};

const DISCIPLINE_FALLBACK_COLOR = "#CCC";

export const getMcBimItemDisplayColor = (args: {
  mcbimItemColorMode: IMcBimItemColorMode;
  item: IMcBimItemDisplayColorSource;
  mmiPath?: string;
}): string | undefined => {
  const { mcbimItemColorMode, item, mmiPath } = args;

  switch (mcbimItemColorMode) {
    case "MULTIPLACER_COLORS": {
      const cls = item.instanceClass as IfcClassesEnum;
      return getInstanceClassMeta(cls).color ?? "#000000";
    }

    case "MMI": {
      const rawMmi = getItemMmiValue(item, mmiPath);
      const mmiValue =
        typeof rawMmi === "string" || typeof rawMmi === "number" || typeof rawMmi === "boolean"
          ? Number(String(rawMmi))
          : NaN;
      return Number.isFinite(mmiValue) ? (MMI_COLORS[mmiValue] ?? "#ccc") : "#ccc";
    }

    case "DISCIPLINE_COLORS": {
      const code = normalizeDisciplineCode(getItemDisciplineValue(item));
      if (!code) return DISCIPLINE_FALLBACK_COLOR;
      return DISCIPLINES[code as keyof typeof DISCIPLINES]?.color ?? DISCIPLINE_FALLBACK_COLOR;
    }

    case "SYSTEM_AND_CLASS":
    case "PRIMARY_SYSTEM": {
      const name = getItemPrimarySystemName(item);
      const kind = inferPrimarySystemKindFromPrimarySystemName(name);
      if (mcbimItemColorMode === "SYSTEM_AND_CLASS" && kind === "OTHER") {
        return getInstanceClassMeta(item.instanceClass as IfcClassesEnum).color ?? "#000000";
      }
      return PRIMARY_SYSTEM_SPECS[kind].color;
    }

    default: {
      const _exhaustive = mcbimItemColorMode;
      return _exhaustive;
    }
  }
};
